import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Card,
  Button,
  Tag,
  Space,
  Typography,
  Spin,
  Descriptions,
  Divider,
  Avatar,
  Select,
  Alert,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  message,
  Flex,
} from 'antd';
import {
  ArrowLeftOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  EditOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuthStore } from '../store/auth';
import {
  getTask,
  updateMyTaskStatus,
  updateAssigneeStatus,
  getTaskComments,
  createComment,
  deleteComment,
  updateTask,
  getAssignableUsers,
  getAssignableGroups,
} from '../lib/api';
import { getUserDisplayName, getUserFullDisplay } from '../lib/user';
import CommentSection from '../components/CommentSection';
import RichTextDisplay from '../components/RichTextDisplay';
import RichTextEditor from '../components/RichTextEditor';

const { Title, Text } = Typography;
const { Option } = Select;

interface Assignee {
  userId: number;
  name?: string;
  username?: string;
  email?: string;
  status: 'unread' | 'read' | 'in_progress' | 'completed';
  assignedAt: string;
  completedAt?: string;
}

interface TaskGroup {
  groupId: number;
  name: string;
}

interface Task {
  id: number;
  title: string;
  description?: string;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'in_progress' | 'completed';
  dueDate?: string;
  createdAt: string;
  creator: {
    id: number;
    name?: string;
    username?: string;
    email?: string;
  };
  assignees: Assignee[];
  groups: TaskGroup[];
}

interface User {
  id: number;
  name?: string;
  username?: string;
  email?: string;
  role: string;
}

interface Group {
  id: number;
  name: string;
  description?: string;
  members: { id: number; name?: string; username?: string; email?: string }[];
}

export default function TaskDetail() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuthStore();
  const [task, setTask] = useState<Task | null>(null);
  const [comments, setComments] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [updating, setUpdating] = useState(false);

  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [editForm, setEditForm] = useState({
    title: '',
    description: '',
    priority: 'medium' as 'high' | 'medium' | 'low',
    dueDate: null as dayjs.Dayjs | null,
    assigneeIds: [] as number[],
    groupIds: [] as number[],
  });

  useEffect(() => {
    loadTask();
    loadComments();
  }, [id]);

  const loadTask = async () => {
    try {
      const data = await getTask(Number(id));
      setTask(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    if (!id) return;
    setCommentsLoading(true);
    try {
      const data = await getTaskComments(Number(id));
      setComments(data);
    } catch (err) {
      console.error(err);
    } finally {
      setCommentsLoading(false);
    }
  };

  const handleAddComment = async (content: string) => {
    if (!id) return;
    await createComment({ taskId: Number(id), content });
    await loadComments();
  };

  const handleDeleteComment = async (commentId: number) => {
    await deleteComment(commentId);
    await loadComments();
  };

  const handleAssigneeStatusChange = async (
    assigneeUserId: number,
    newStatus: string
  ) => {
    if (!task) return;
    setUpdating(true);
    try {
      const updatedTask = await updateAssigneeStatus(
        task.id,
        assigneeUserId,
        newStatus
      );
      setTask(updatedTask);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  const handleMyStatusChange = async (newStatus: string) => {
    if (!task) return;
    setUpdating(true);
    try {
      const updatedTask = await updateMyTaskStatus(task.id, newStatus);
      setTask(updatedTask);
    } catch (err) {
      console.error(err);
    } finally {
      setUpdating(false);
    }
  };

  // Check if user can edit this task
  const canEditTask = () => {
    if (!task || !user) return false;
    if (user.role === 'boss') return true;
    if (user.role === 'manager') {
      // Manager can edit if they created the task
      if (task.creator.id === user.id) return true;
      // Or if task is assigned to their subordinates
      // (This would require checking subordinates, simplified here)
      return false;
    }
    return false;
  };

  const enterEditMode = async () => {
    if (!task) return;
    setEditLoading(true);
    try {
      const [usersData, groupsData] = await Promise.all([
        getAssignableUsers(),
        getAssignableGroups(),
      ]);
      setUsers(usersData);
      setGroups(groupsData);

      setEditForm({
        title: task.title,
        description: task.description || '',
        priority: task.priority,
        dueDate: task.dueDate ? dayjs(task.dueDate) : null,
        assigneeIds: task.assignees.map((a) => a.userId),
        groupIds: task.groups.map((g) => g.groupId),
      });
      setIsEditing(true);
    } catch (err: any) {
      message.error(err.message || '無法載入編輯資料');
    } finally {
      setEditLoading(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!task) return;

    // Validate at least one assignee or group
    if (editForm.assigneeIds.length === 0 && editForm.groupIds.length === 0) {
      message.error('至少需要指派一名員工或一個群組');
      return;
    }

    setEditLoading(true);
    try {
      const updated = await updateTask(task.id, {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        dueDate: editForm.dueDate?.toISOString() || null,
        assigneeIds: editForm.assigneeIds,
        groupIds: editForm.groupIds,
      });
      setTask(updated);
      setIsEditing(false);
      message.success('任務已更新');
    } catch (err: any) {
      message.error(err.message || '更新失敗');
    } finally {
      setEditLoading(false);
    }
  };

  const cancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      title: '',
      description: '',
      priority: 'medium',
      dueDate: null,
      assigneeIds: [],
      groupIds: [],
    });
  };

  const getStatusTag = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      unread: { color: 'default', label: '未讀' },
      read: { color: 'blue', label: '已讀' },
      in_progress: { color: 'processing', label: '進行中' },
      completed: { color: 'success', label: '已完成' },
    };
    const { color, label } = config[status] || config.unread;
    return <Tag color={color}>{label}</Tag>;
  };

  const getTaskStatusTag = (status: string) => {
    const config: Record<string, { color: string; label: string }> = {
      pending: { color: 'default', label: '待處理' },
      in_progress: { color: 'processing', label: '進行中' },
      completed: { color: 'success', label: '已完成' },
    };
    const { color, label } = config[status] || config.pending;
    return <Tag color={color}>{label}</Tag>;
  };

  const getPriorityTag = (priority: string) => {
    const config: Record<string, { color: string; label: string }> = {
      high: { color: 'red', label: '高優先級' },
      medium: { color: 'orange', label: '中優先級' },
      low: { color: 'green', label: '低優先級' },
    };
    const { color, label } = config[priority] || config.medium;
    return <Tag color={color}>{label}</Tag>;
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!task) {
    return (
      <Card>
        <Alert
          title="任務不存在"
          description={<Link to="/tasks">返回任務列表</Link>}
          type="error"
          showIcon
        />
      </Card>
    );
  }

  const myAssignment = task.assignees.find((a) => a.userId === user?.id);
  const canUpdateStatus = myAssignment && myAssignment.status !== 'completed';

  return (
    <Space orientation="vertical" style={{ width: '100%' }} size="large">
      <Link to="/tasks">
        <Button icon={<ArrowLeftOutlined />}>返回任務列表</Button>
      </Link>

      <Card
        title={
          <Space>
            <Title level={4} style={{ margin: 0 }}>
              {task.title}
            </Title>
            {getPriorityTag(task.priority)}
            {getTaskStatusTag(task.status)}
            {canEditTask() && !isEditing && (
              <Button
                icon={<EditOutlined />}
                onClick={enterEditMode}
                loading={editLoading}
                size="small"
              >
                編輯
              </Button>
            )}
          </Space>
        }
      >
        {isEditing ? (
          // Editing Mode
          <Form layout="vertical">
            <Form.Item label="任務名稱" required>
              <Input
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
                placeholder="輸入任務名稱"
              />
            </Form.Item>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label="優先級">
                  <Select
                    value={editForm.priority}
                    onChange={(value) =>
                      setEditForm({ ...editForm, priority: value })
                    }
                  >
                    <Option value="high">高</Option>
                    <Option value="medium">中</Option>
                    <Option value="low">低</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="截止日期">
                  <DatePicker
                    style={{ width: '100%' }}
                    value={editForm.dueDate}
                    onChange={(date) =>
                      setEditForm({ ...editForm, dueDate: date })
                    }
                  />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="指派對象" required>
              <Text type="secondary">選擇個別員工或群組（可複選）</Text>

              {groups.length > 0 && (
                <Select
                  mode="multiple"
                  placeholder="選擇群組"
                  style={{ width: '100%', marginTop: 8 }}
                  value={editForm.groupIds}
                  onChange={(value) =>
                    setEditForm({ ...editForm, groupIds: value })
                  }
                  optionLabelProp="label"
                >
                  {groups.map((group) => (
                    <Option
                      key={group.id}
                      value={group.id}
                      label={group.name}
                    >
                      <div>
                        <div>{group.name}</div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {group.description || `${group.members.length} 位成員`}
                        </Text>
                      </div>
                    </Option>
                  ))}
                </Select>
              )}

              <Select
                mode="multiple"
                placeholder="選擇個別員工"
                style={{ width: '100%', marginTop: 8 }}
                value={editForm.assigneeIds}
                onChange={(value) =>
                  setEditForm({ ...editForm, assigneeIds: value })
                }
                optionLabelProp="label"
              >
                {users.map((u) => (
                  <Option key={u.id} value={u.id} label={getUserDisplayName(u)}>
                    {getUserDisplayName(u)}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {/* Preview of selected assignees */}
            {(editForm.assigneeIds.length > 0 || editForm.groupIds.length > 0) && (
              <Card
                size="small"
                title="已選擇的指派對象"
                style={{ background: '#f6ffed', marginBottom: 24 }}
              >
                <Space size="small" wrap>
                  {editForm.groupIds.map((groupId) => {
                    const group = groups.find((g) => g.id === groupId);
                    return group ? (
                      <Tag key={groupId} color="purple">
                        群組: {group.name}
                      </Tag>
                    ) : null;
                  })}
                  {editForm.assigneeIds.map((userId) => {
                    const u = users.find((user) => user.id === userId);
                    return u ? (
                      <Tag key={userId} color="blue">
                        {getUserDisplayName(u)}
                      </Tag>
                    ) : null;
                  })}
                </Space>
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">
                    共{' '}
                    {(() => {
                      const assigneeIds = new Set<number>(
                        editForm.assigneeIds
                      );
                      editForm.groupIds.forEach((groupId) => {
                        const group = groups.find((g) => g.id === groupId);
                        group?.members.forEach((m) => assigneeIds.add(m.id));
                      });
                      return assigneeIds.size;
                    })()}{' '}
                    人將收到此任務
                  </Text>
                </div>
              </Card>
            )}

            <Form.Item label="任務描述">
              <RichTextEditor
                value={editForm.description}
                onChange={(content) =>
                  setEditForm({ ...editForm, description: content })
                }
                height={200}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button onClick={cancelEdit} icon={<CloseOutlined />}>
                  取消
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveEdit}
                  loading={editLoading}
                  icon={<SaveOutlined />}
                  disabled={
                    editForm.assigneeIds.length + editForm.groupIds.length ===
                    0
                  }
                >
                  保存
                </Button>
              </Space>
            </Form.Item>
          </Form>
        ) : (
          // View Mode
          <>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="建立者">
                <Space>
                  <UserOutlined />
                  {getUserFullDisplay(task.creator)}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="截止日期">
                <Space>
                  <ClockCircleOutlined />
                  {task.dueDate
                    ? new Date(task.dueDate).toLocaleDateString('zh-TW')
                    : '無期限'}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="建立時間">
                {new Date(task.createdAt).toLocaleString('zh-TW')}
              </Descriptions.Item>
            </Descriptions>

            {/* Groups */}
            {task.groups && task.groups.length > 0 && (
              <>
                <Divider />
                <div>
                  <Text strong>指派的群組</Text>
                  <div style={{ marginTop: 8 }}>
                    <Space>
                      {task.groups.map((g) => (
                        <Tag key={g.groupId} color="purple">
                          {g.name}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                </div>
              </>
            )}

            {/* Assignees */}
            <Divider />
            <div>
              <Text strong>被指派人 ({task.assignees.length}人)</Text>
              <div style={{ marginTop: 8 }}>
                <Space orientation="vertical" style={{ width: '100%' }}>
                  {task.assignees.map((assignee) => (
                    <Flex
                      key={assignee.userId}
                      justify="space-between"
                      align="center"
                      style={{
                        padding: '12px 0',
                        borderBottom: '1px solid #f0f0f0',
                      }}
                    >
                      <Flex gap={12} align="center">
                        <Avatar icon={<UserOutlined />} />
                        <span>{getUserDisplayName(assignee)}</span>
                      </Flex>
                      <Flex gap={8} align="center">
                        {getStatusTag(assignee.status)}
                        {user?.role !== 'employee' &&
                          user?.id !== assignee.userId &&
                          assignee.status !== 'completed' && (
                            <Select
                              size="small"
                              style={{ width: 120 }}
                              value={assignee.status}
                              onChange={(value) =>
                                handleAssigneeStatusChange(assignee.userId, value)
                              }
                              disabled={updating}
                            >
                              <Option value="unread">未讀</Option>
                              <Option value="read">已讀</Option>
                              <Option value="in_progress">進行中</Option>
                              <Option value="completed">已完成</Option>
                            </Select>
                          )}
                      </Flex>
                    </Flex>
                  ))}
                </Space>
              </div>
            </div>

            {/* Description */}
            <Divider />
            <div>
              <Text strong>任務描述</Text>
              <div style={{ marginTop: 8, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
                <RichTextDisplay content={task.description || ''} emptyText="無描述" />
              </div>
            </div>
          </>
        )}

        {/* Employee's own status update */}
        {user?.role === 'employee' && canUpdateStatus && (
          <>
            <Divider />
            <Alert
              title="更新我的狀態"
              description={
                <Space>
                  {myAssignment.status === 'unread' && (
                    <Button onClick={() => handleMyStatusChange('read')} loading={updating}>
                      標記為已讀
                    </Button>
                  )}
                  {(myAssignment.status === 'unread' || myAssignment.status === 'read') && (
                    <Button
                      type="primary"
                      onClick={() => handleMyStatusChange('in_progress')}
                      loading={updating}
                    >
                      開始進行
                    </Button>
                  )}
                </Space>
              }
              type="info"
              showIcon
            />
          </>
        )}
      </Card>

      {/* Comments Section */}
      <Card title={<><FileTextOutlined /> 討論區</>}>
        <CommentSection
          comments={comments}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          loading={commentsLoading}
        />
      </Card>
    </Space>
  );
}
